import { jalaliPayMonthOf, type PayRunItem, type Payslip } from "@kitchen/schemas";
import { Button, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, hrPayrollApi } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

function formatRial(amount: number): string {
  return new Intl.NumberFormat("fa-IR").format(amount) + " ریال";
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${String(m)} دقیقه`;
  if (m === 0) return `${String(h)} ساعت`;
  return `${String(h)} ساعت و ${String(m)} دقیقه`;
}

/** The current Jalali month in Tehran: pay runs are by Jalali month. */
function currentPayMonth(): { year: number; month: number } {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran" }).format(new Date());
  return jalaliPayMonthOf(today);
}

export function PayTab() {
  const [showComputeModal, setShowComputeModal] = useState(false);
  const [computeYear, setComputeYear] = useState<number>(() => currentPayMonth().year);
  const [computeMonth, setComputeMonth] = useState<number>(() => currentPayMonth().month);
  const [computeNotes, setComputeNotes] = useState<string>("");

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [payslipError, setPayslipError] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ["hr", "payroll", "runs"],
    queryFn: () => hrPayrollApi.listPayRuns(),
  });

  const selectedRunQuery = useQuery({
    queryKey: ["hr", "payroll", "run", selectedRunId],
    queryFn: () => (selectedRunId ? hrPayrollApi.getPayRun(selectedRunId) : null),
    enabled: Boolean(selectedRunId),
  });

  const computeAction = useAction(async () => {
    const run = await hrPayrollApi.createPayRun({
      year: computeYear,
      month: computeMonth,
      notes: computeNotes.trim(),
    });
    setShowComputeModal(false);
    setSelectedRunId(run.id);
  }, [["hr", "payroll", "runs"]]);

  const approveAction = useAction<string>(
    async (id: string) => {
      await hrPayrollApi.approvePayRun(id);
    },
    [
      ["hr", "payroll", "runs"],
      ["hr", "payroll", "run", selectedRunId ?? ""],
    ],
  );

  async function handleOpenPayslip(personId: string, payRunId: string) {
    setPayslipError(null);
    try {
      const slip = await hrPayrollApi.getPayslip(personId, payRunId);
      setSelectedPayslip(slip);
    } catch (err) {
      setPayslipError(err instanceof ApiError ? err.message : "خطا در دریافت فیش حقوقی");
    }
  }

  const runs = runsQuery.data ?? [];
  const activeRun = selectedRunQuery.data ?? runs[0] ?? null;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top action panel */}
      <Panel
        title="محاسبه و مدیریت حقوق و دستمزد پرسنل"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setShowComputeModal(true);
            }}
            id="btn-compute-pay-run"
          >
            محاسبه ران حقوق ماهانه جدید
          </Button>
        }
      >
        <p className="muted" style={{ margin: "0 0 var(--s3) 0" }}>
          محاسبه دقیق کارکرد ماهانه، اضافه‌کاری، تعطیل‌کاری، جمعه‌کاری، شب‌کاری، مزایا و کسورات بر اساس تردد
          واقعی و انجماد قطعی دوره‌ها (§۱۱.۵)
        </p>

        {runsQuery.isError && (
          <ErrorLine
            error={runsQuery.error instanceof Error ? runsQuery.error.message : String(runsQuery.error)}
          />
        )}
        {payslipError && <ErrorLine error={payslipError} />}

        {runs.length === 0 ? (
          <p className="muted">
            هیچ دوره حقوقی محاسبه نشده است. برای شروع دکمه محاسبه ران حقوق ماهانه را بزنید.
          </p>
        ) : (
          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table className="table" style={{ width: "100%", textAlign: "right" }}>
              <thead>
                <tr>
                  <th>دوره ماهانه</th>
                  <th>وضعیت دوره</th>
                  <th>تعداد پرسنل</th>
                  <th>سرجمع ناخالص</th>
                  <th>سرجمع خالص پرداختی</th>
                  <th>تاریخ تأیید</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const isApproved = r.status === "approved";
                  const isSelected = activeRun?.id === r.id;
                  return (
                    <tr
                      key={r.id}
                      style={{
                        background: isSelected ? "var(--surface-hover, rgba(0,0,0,0.04))" : undefined,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedRunId(r.id);
                      }}
                    >
                      <td>
                        <strong>
                          {r.year} / {String(r.month).padStart(2, "0")}
                        </strong>
                      </td>
                      <td>
                        <span className={`chip ${isApproved ? "green" : "blue"}`}>
                          {isApproved ? "تأیید و منجمد شده" : "پیش‌نویس (قابل محاسبه مجدد)"}
                        </span>
                      </td>
                      <td>{r.itemsCount} نفر</td>
                      <td>{formatRial(r.totalGrossRial)}</td>
                      <td>
                        <strong>{formatRial(r.totalNetRial)}</strong>
                      </td>
                      <td>{r.approvedAt ? new Date(r.approvedAt).toLocaleDateString("fa-IR") : "—"}</td>
                      <td>
                        <Button
                          variant={isSelected ? "primary" : "default"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRunId(r.id);
                          }}
                          className="btn-open-run-details"
                        >
                          مشاهده لیست
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Selected Pay Run Details */}
      {activeRun && (
        <Panel
          title={`جزئیات دوره حقوق: ${String(activeRun.year)} / ${String(activeRun.month).padStart(2, "0")}`}
          actions={
            <div className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
              {activeRun.status === "draft" && (
                <Button
                  variant="primary"
                  onClick={() => {
                    approveAction.run(activeRun.id);
                  }}
                  disabled={approveAction.busy}
                  id="btn-approve-pay-run"
                >
                  تأیید نهایی و انجماد دوره (Approve)
                </Button>
              )}
              {activeRun.status === "approved" && (
                <span className="chip green" style={{ padding: "var(--s2) var(--s3)" }}>
                  ✓ دوره منجمد شده است
                </span>
              )}
            </div>
          }
        >
          {approveAction.error && <ErrorLine error={approveAction.error} />}

          <div
            className="row wrap"
            style={{
              gap: "var(--s4)",
              marginBottom: "var(--s4)",
              padding: "var(--s3)",
              background: "var(--surface-muted, rgba(0,0,0,0.02))",
              borderRadius: "var(--r-md)",
            }}
          >
            <div>
              <span className="muted">سرجمع پایه:</span>{" "}
              <strong>{formatRial(activeRun.totalBaseRial)}</strong>
            </div>
            <div>
              <span className="muted">اضافه‌کاری:</span>{" "}
              <strong>{formatRial(activeRun.totalOvertimeRial)}</strong>
            </div>
            <div>
              <span className="muted">تعطیل و جمعه‌کاری:</span>{" "}
              <strong>{formatRial(activeRun.totalHolidayRial)}</strong>
            </div>
            <div>
              <span className="muted">مزایا:</span> <strong>{formatRial(activeRun.totalBenefitsRial)}</strong>
            </div>
            <div>
              <span className="muted">کسورات:</span>{" "}
              <strong>{formatRial(activeRun.totalDeductionsRial)}</strong>
            </div>
            <div>
              <span className="muted">اصلاحیه‌ها:</span>{" "}
              <strong>{formatRial(activeRun.totalAdjustmentsRial)}</strong>
            </div>
            <div>
              <span className="muted">خالص کل:</span>{" "}
              <strong style={{ color: "var(--color-primary, #2563eb)" }}>
                {formatRial(activeRun.totalNetRial)}
              </strong>
            </div>
          </div>

          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table className="table" style={{ width: "100%", textAlign: "right" }}>
              <thead>
                <tr>
                  <th>نام پرسنل</th>
                  <th>سمت</th>
                  <th>کارکرد / اضافه‌کاری</th>
                  <th>تعطیل‌کاری</th>
                  <th>حقوق پایه</th>
                  <th>مزایا / کسورات</th>
                  <th>خالص پرداختی</th>
                  <th>فیش حقوقی</th>
                </tr>
              </thead>
              <tbody>
                {activeRun.items?.map((it: PayRunItem) => (
                  <tr key={it.id}>
                    <td>
                      <strong>{it.personName}</strong>
                    </td>
                    <td>{it.positionName || "—"}</td>
                    <td>
                      <div>{formatMinutes(it.workedMinutes)}</div>
                      {it.overtimeMinutes > 0 && (
                        <small className="muted">+{formatMinutes(it.overtimeMinutes)} اضافه</small>
                      )}
                    </td>
                    <td>{it.holidayMinutes > 0 ? formatMinutes(it.holidayMinutes) : "—"}</td>
                    <td>{formatRial(it.baseRial)}</td>
                    <td>
                      <div>+{formatRial(it.benefitsRial)}</div>
                      {it.deductionsRial > 0 && (
                        <div style={{ color: "var(--color-danger, #ef4444)" }}>
                          -{formatRial(it.deductionsRial)}
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{formatRial(it.netRial)}</strong>
                    </td>
                    <td>
                      <Button
                        variant="default"
                        onClick={() => {
                          void handleOpenPayslip(it.personId, activeRun.id);
                        }}
                        className="btn-view-payslip"
                      >
                        مشاهده فیش
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Compute modal */}
      {showComputeModal && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="modal card"
            style={{
              maxWidth: 500,
              width: "90%",
              padding: "var(--s4)",
              background: "white",
              borderRadius: "var(--r-lg)",
            }}
          >
            <h3>محاسبه ران حقوق ماهانه جدید</h3>
            <p className="muted" style={{ margin: "var(--s2) 0 var(--s3) 0" }}>
              ورود سال و ماه برای استخراج ترددها، ساعات کارکرد و محاسبه اقلام حقوق
            </p>

            {computeAction.error && <ErrorLine error={computeAction.error} />}

            <div className="stack" style={{ gap: "var(--s3)" }}>
              <Field
                id="compute-year-input"
                label="سال شمسی (مثلاً ۱۴۰۵)"
                type="number"
                value={computeYear}
                onChange={(e) => {
                  setComputeYear(Number(e.target.value));
                }}
              />

              <Field
                id="compute-month-input"
                label="ماه شمسی (۱ فروردین تا ۱۲ اسفند)"
                type="number"
                min={1}
                max={12}
                value={computeMonth}
                onChange={(e) => {
                  setComputeMonth(Number(e.target.value));
                }}
              />

              <Field
                id="compute-notes-input"
                label="یادداشت دوره"
                type="text"
                value={computeNotes}
                onChange={(e) => {
                  setComputeNotes(e.target.value);
                }}
                placeholder="مثال: ران حقوق آبان ماه ۱۴۰۵"
              />

              <div
                className="row"
                style={{ gap: "var(--s2)", marginTop: "var(--s3)", justifyContent: "flex-end" }}
              >
                <Button
                  variant="default"
                  onClick={() => {
                    setShowComputeModal(false);
                  }}
                >
                  انصراف
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    computeAction.run(undefined);
                  }}
                  disabled={computeAction.busy}
                  id="btn-submit-compute-pay-run"
                >
                  محاسبه و ایجاد پیش‌نویس
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payslip View Modal (A4 print layout) */}
      {selectedPayslip && (
        <div
          className="modal-backdrop"
          id="payslip-modal"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="modal card"
            style={{
              maxWidth: 700,
              width: "95%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "var(--s4)",
              background: "white",
              borderRadius: "var(--r-lg)",
            }}
          >
            <div
              className="row"
              style={{
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid var(--border)",
                paddingBottom: "var(--s3)",
                marginBottom: "var(--s3)",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>فیش حقوق و دستمزد پرسنل</h3>
                <small className="muted">
                  دوره: {selectedPayslip.year} / {String(selectedPayslip.month).padStart(2, "0")}
                </small>
              </div>
              <div className="row" style={{ gap: "var(--s2)" }}>
                <Button
                  variant="default"
                  onClick={() => {
                    window.print();
                  }}
                >
                  چاپ A4
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    setSelectedPayslip(null);
                  }}
                  id="btn-close-payslip"
                >
                  بستن
                </Button>
              </div>
            </div>

            {/* Employee info */}
            <div
              className="grid"
              style={{
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--s3)",
                padding: "var(--s3)",
                background: "var(--surface-muted, rgba(0,0,0,0.02))",
                borderRadius: "var(--r-md)",
                marginBottom: "var(--s3)",
              }}
            >
              <div>
                <span className="muted">نام پرسنل:</span> <strong>{selectedPayslip.personName}</strong>
              </div>
              <div>
                <span className="muted">سمت:</span> <strong>{selectedPayslip.positionName || "—"}</strong>
              </div>
              <div>
                <span className="muted">نوع قرارداد:</span> <strong>{selectedPayslip.employmentKind}</strong>
              </div>
              <div>
                <span className="muted">وضعیت فیش:</span>{" "}
                <span className={`chip ${selectedPayslip.status === "approved" ? "green" : "blue"}`}>
                  {selectedPayslip.status === "approved" ? "تأیید و قطعی شده" : "پیش‌نویس"}
                </span>
              </div>
            </div>

            {/* Work summary hours */}
            <div style={{ marginBottom: "var(--s3)" }}>
              <h4 style={{ margin: "0 0 var(--s2) 0" }}>خلاصه کارکرد ماه</h4>
              <div className="row wrap" style={{ gap: "var(--s3)", fontSize: "0.9rem" }}>
                <div>
                  کارکرد: <strong>{formatMinutes(selectedPayslip.workedMinutes)}</strong>
                </div>
                {selectedPayslip.overtimeMinutes > 0 && (
                  <div>
                    اضافه‌کاری: <strong>{formatMinutes(selectedPayslip.overtimeMinutes)}</strong>
                  </div>
                )}
                {selectedPayslip.holidayMinutes > 0 && (
                  <div>
                    تعطیل‌کاری: <strong>{formatMinutes(selectedPayslip.holidayMinutes)}</strong>
                  </div>
                )}
                {selectedPayslip.lateMinutes > 0 && (
                  <div>
                    تاخیر ورود:{" "}
                    <strong style={{ color: "var(--color-danger, #ef4444)" }}>
                      {String(selectedPayslip.lateMinutes)} دقیقه
                    </strong>
                  </div>
                )}
                {selectedPayslip.leaveDays > 0 && (
                  <div>
                    مرخصی: <strong>{String(selectedPayslip.leaveDays)} روز</strong>
                  </div>
                )}
              </div>
            </div>

            {/* Earnings lines */}
            <div style={{ marginBottom: "var(--s3)" }}>
              <h4 style={{ margin: "0 0 var(--s2) 0" }}>مزایا و دریافتی‌ها</h4>
              <table className="table" style={{ width: "100%", textAlign: "right" }}>
                <thead>
                  <tr>
                    <th>شرح آیتم</th>
                    <th>مقدار / ضریب</th>
                    <th>مبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPayslip.earningsLines.map((line, idx) => (
                    <tr key={idx}>
                      <td>{line.title}</td>
                      <td>
                        {line.quantity} {line.note ? `(${line.note})` : ""}
                      </td>
                      <td>
                        <strong>{formatRial(line.amountRial)}</strong>
                      </td>
                    </tr>
                  ))}
                  {selectedPayslip.adjustmentLines.map((line, idx) => (
                    <tr key={`adj-${String(idx)}`} style={{ background: "rgba(34, 197, 94, 0.05)" }}>
                      <td>{line.title}</td>
                      <td>—</td>
                      <td>
                        <strong>+{formatRial(line.amountRial)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Deductions lines */}
            {selectedPayslip.deductionLines.length > 0 && (
              <div style={{ marginBottom: "var(--s3)" }}>
                <h4 style={{ margin: "0 0 var(--s2) 0" }}>کسورات قانونی و توافقی</h4>
                <table className="table" style={{ width: "100%", textAlign: "right" }}>
                  <thead>
                    <tr>
                      <th>شرح کسر</th>
                      <th>مبلغ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPayslip.deductionLines.map((line, idx) => (
                      <tr key={idx}>
                        <td>{line.title}</td>
                        <td style={{ color: "var(--color-danger, #ef4444)" }}>
                          <strong>-{formatRial(line.amountRial)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals box */}
            <div
              style={{
                padding: "var(--s3)",
                background: "var(--surface-muted, rgba(0,0,0,0.04))",
                borderRadius: "var(--r-md)",
                marginTop: "var(--s3)",
              }}
            >
              <div className="row" style={{ justifyContent: "space-between", marginBottom: "var(--s2)" }}>
                <span>مجموع ناخالص دریافتی:</span>
                <strong>{formatRial(selectedPayslip.grossRial)}</strong>
              </div>
              <div
                className="row"
                style={{
                  justifyContent: "space-between",
                  fontSize: "1.1rem",
                  borderTop: "1px dashed var(--border)",
                  paddingTop: "var(--s2)",
                }}
              >
                <span>خالص پرداختی نهایی:</span>
                <strong style={{ color: "var(--color-primary, #2563eb)" }}>
                  {formatRial(selectedPayslip.netRial)}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
